import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Checkbox,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiUrl, handleApiError } from './utils/api';
import NoMatchingRecords from './components/NoMatchingRecords';
import { useNotify } from './components/NotificationProvider';
import { useConfirm } from './components/ConfirmProvider';
import { useRole } from '../auth/RoleContext';

/**
 * Ticket Intake — the office side of the public, no-login driver upload page.
 *
 * The product this screen serves ends here: tickets come in from drivers' phones, a human confirms
 * what the model read, and the accepted rows leave as an Excel file. There is no invoicing behind it,
 * so ACCEPTED is a terminal state and the Export button is the actual deliverable.
 *
 * Two decisions worth keeping:
 *  - The scan sits BESIDE the fields while you edit. Every correction here is someone reading a photo
 *    of a paper ticket, so making them open the image in another tab would be the whole job.
 *  - An unresolved truck is not an error. These are ~100 subhaulers whose trucks are in no truck
 *    master; the raw string off the paper is all the attribution that exists, and it is editable.
 */

// Two states, not three. A clean scan is ACCEPTED on arrival — there is no "read but not yet
// signed off" middle step, because it only ever meant a person had to approve ~4,000 machine-correct
// tickets a week before any of them could be exported. Whether a person or the machine accepted it
// is visible in the "Accepted by" column, which is the part that actually matters for audit.
const STATUSES = [
  { key: 'REVIEW', label: 'Needs review', color: 'warning' },
  { key: 'ACCEPTED', label: 'Accepted', color: 'success' },
  { key: '', label: 'All', color: 'default' },
];

const statusColor = (s) => (STATUSES.find((x) => x.key === s) || {}).color || 'default';

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Blank -> null so an emptied field clears the value rather than storing "". */
const nullIfBlank = (v) => (v === '' || v === undefined ? null : v);

/**
 * MM/DD/YYYY — the format used everywhere else in the product (the settlement PDFs, the tickets
 * grid, and the date pickers directly above this table).
 *
 * Sliced as text rather than parsed into a Date on purpose. `new Date('2026-08-13')` is midnight
 * **UTC**, which in US Central renders as 08/12 — every date in the table would silently show a day
 * early. These are payment records; a ticket dated a day off is worth getting right.
 */
function usDate(value) {
  if (!value) return '—';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : '—';
}

/** h:mm AM/PM, matching the settlement sheet. Times arrive as HH:mm:ss. */
function usTime(value) {
  if (!value) return '—';
  const [hh, mm] = String(value).split(':');
  const h = Number(hh);
  if (Number.isNaN(h)) return '—';
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${mm} ${suffix}`;
}

/** A real Date for Excel, so the office can sort and filter the column instead of getting text. */
function excelDate(value) {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : '';
}

export default function TicketIntake() {
  const notify = useNotify();
  const confirm = useConfirm();
  // Who is signed in. Previously this read localStorage('viai.email') — a key nothing in the app
  // ever writes — so every human acceptance was stored with a null reviewer and displayed as
  // "Automatic", making the audit column claim the machine had approved things a person approved.
  const { user } = useRole();

  const [startDate, setStartDate] = useState(isoDaysAgo(7));
  const [endDate, setEndDate] = useState(isoToday());
  // ALL by default. Needs-review used to be the landing tab because the truck-master rule sent
  // nearly every ticket there — but a clean scan is now accepted on arrival, so that tab is empty on
  // a normal day and opening onto "no matching records" reads like the screen is broken. The office's
  // usual question is "what came in", and the orange chip still shows the moment anything needs a
  // person, so nothing gets buried by defaulting here.
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null);   // the row open in the dialog
  const [draft, setDraft] = useState({});
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);   // ids ticked for bulk delete
  const [deleting, setDeleting] = useState(false);

  // The review queue is deliberately NOT date-filtered.
  //
  // A ticket lands in review precisely because something couldn't be read — and one of the things
  // that can't be read is the DATE. Filtering the queue by the ticket's own date therefore hides the
  // rows that most need attention: a misread or post-dated ticket falls outside any range you pick,
  // so the "Needs review: 1" chip (which counts everything) contradicts an empty table, and no
  // amount of widening the start date finds it. Seen live on ticket 1046404553, read as 08/26 while
  // the range ended 08/22.
  //
  // The tickets screen already learned this — see TicketService.getReviewQueue, which is undated for
  // the same reason. Accepted/Read rows have been confirmed by a human, so dates are meaningful there.
  const dateFiltered = status !== 'REVIEW';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listReq = status === 'REVIEW'
        ? axios.get(`${apiUrl}/api/uploads/review-queue`)
        : axios.get(`${apiUrl}/api/uploads`, { params: { startDate, endDate, status: status || undefined } });
      const [list, c] = await Promise.all([
        listReq,
        axios.get(`${apiUrl}/api/uploads/counts`, { params: { startDate, endDate } }),
      ]);
      setRows(Array.isArray(list.data) ? list.data : []);
      setCounts(c.data || null);
    } catch (e) {
      setError(handleApiError(e, 'Could not load ticket intake'));
      setRows([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, [startDate, endDate, status]);

  useEffect(() => { load(); }, [load]);

  // Drop the selection whenever the filter changes. Ticked rows that scroll out of view are still
  // ticked, and deleting rows you can no longer see is not something anyone means to do.
  useEffect(() => { setSelected([]); }, [startDate, endDate, status]);

  // The image is fetched as a blob through axios rather than set as a bare <img src>, so the
  // X-Tenant-User header goes with it. A plain src would carry no identity and 404.
  const openRow = async (row) => {
    setEditing(row);
    setDraft({
      ticketNo: row.ticketNo || '',
      truckNoRaw: row.truckNoRaw || '',
      quantity: row.quantity ?? '',
      ticketDate: row.ticketDate || '',
      ticketTime: row.ticketTime ? String(row.ticketTime).slice(0, 5) : '',
      job: row.job || '',
      pit: row.pit || '',
    });
    setImageUrl(null);
    if (!row.hasImage) return;
    setImageLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/uploads/${row.id}/image`, { responseType: 'blob' });
      setImageUrl(URL.createObjectURL(res.data));
    } catch (e) {
      notify.error(handleApiError(e, 'Could not load the ticket photo'));
    } finally {
      setImageLoading(false);
    }
  };

  const closeRow = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(null);
    setEditing(null);
    setDraft({});
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/api/uploads/${editing.id}`, {
        ticketNo: nullIfBlank(draft.ticketNo),
        truckNoRaw: nullIfBlank(draft.truckNoRaw),
        quantity: draft.quantity === '' ? null : Number(draft.quantity),
        ticketDate: nullIfBlank(draft.ticketDate),
        ticketTime: draft.ticketTime ? `${draft.ticketTime}:00` : null,
        job: nullIfBlank(draft.job),
        pit: nullIfBlank(draft.pit),
      });
      notify.success('Ticket updated');
      return true;
    } catch (e) {
      notify.error(handleApiError(e, 'Could not save the ticket'));
      return false;
    } finally {
      setSaving(false);
      await load();
    }
  };

  /** Save then accept — the reviewer's edits and their sign-off are one action, not two. */
  const saveAndAccept = async () => {
    if (!(await saveDraft())) return;
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/api/uploads/${editing.id}/accept`, null,
        { params: { email: user?.email || undefined } });
      notify.success(`Ticket ${draft.ticketNo || editing.id} accepted`);
      closeRow();
    } catch (e) {
      // The backend refuses to accept a row missing a column the export needs, and says which.
      // The dialog deliberately stays OPEN on failure so the reviewer can fix the field it named.
      notify.error(handleApiError(e, 'Could not accept the ticket'));
    } finally {
      setSaving(false);
      await load();
    }
  };

  const reopen = async (row) => {
    const yes = await confirm({
      title: 'Send back to review?',
      message: `Ticket ${row.ticketNo || row.id} will return to the review queue.`,
      confirmText: 'Send back',
    });
    if (!yes) return;
    try {
      await axios.post(`${apiUrl}/api/uploads/${row.id}/reopen`, null,
        { params: { reason: 'Reopened by the office' } });
      notify.success('Sent back to review');
    } catch (e) {
      notify.error(handleApiError(e, 'Could not reopen the ticket'));
    } finally {
      await load();
    }
  };

  /**
   * Remove an upload and its photo.
   *
   * The confirmation names the ticket rather than asking "are you sure?" — at a glance down a list of
   * near-identical rows, the number is the only thing that tells you which one you're about to lose.
   * An ACCEPTED row gets a sterner warning because it has already been signed off and may have been
   * exported and billed from; it is also irreversible, since the photo goes with it.
   */
  const remove = async (row) => {
    const wasAccepted = row.status === 'ACCEPTED';
    const yes = await confirm({
      title: 'Delete this ticket?',
      severity: 'error',
      confirmText: 'Delete',
      message: wasAccepted
        ? `Ticket ${row.ticketNo || row.id} has already been accepted and may have been exported. `
          + 'Deleting removes the record and the driver’s photo permanently. This cannot be undone.'
        : `Ticket ${row.ticketNo || row.id} and the driver’s photo will be deleted permanently. `
          + 'This cannot be undone.',
    });
    if (!yes) return;
    try {
      await axios.delete(`${apiUrl}/api/uploads/${row.id}`);
      notify.success(`Ticket ${row.ticketNo || row.id} deleted`);
    } catch (e) {
      notify.error(handleApiError(e, 'Could not delete the ticket'));
    } finally {
      // Refetch whatever happened. On failure the screen must NOT keep showing what it showed
      // before: a delete can succeed server-side and still fail on the way back (dropped response,
      // timeout), and a row that is gone but still on screen invites a second delete of an id that
      // no longer exists. Reconciling with the server is the only honest thing to display.
      await load();
    }
  };

  const visibleIds = rows.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const someVisibleSelected = selected.length > 0 && !allVisibleSelected;

  const toggleAll = () => setSelected(allVisibleSelected ? [] : visibleIds);
  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  /**
   * Delete every ticked ticket, and their photos.
   *
   * Uses the existing per-row endpoint rather than a new bulk one, matching how the master screens
   * already do this — but <b>five at a time</b>, not all at once. Each delete now also removes a GCS
   * object, so firing fifty in parallel would put fifty concurrent storage calls on an instance that
   * accepts twenty concurrent requests, and the deletes would start timing out and failing
   * *partially* — the worst outcome, because the operator can't tell which ones went.
   *
   * Failures are counted and reported rather than swallowed: a partial bulk delete that claims
   * success is how rows quietly survive.
   */
  const bulkDelete = async () => {
    if (selected.length === 0) return;
    const acceptedCountInSelection = rows.filter(
      (r) => selected.includes(r.id) && r.status === 'ACCEPTED').length;

    const yes = await confirm({
      title: `Delete ${selected.length} ticket${selected.length === 1 ? '' : 's'}?`,
      severity: 'error',
      confirmText: `Delete ${selected.length}`,
      message: acceptedCountInSelection > 0
        ? `${acceptedCountInSelection} of these ${acceptedCountInSelection === 1 ? 'has' : 'have'} `
          + 'already been accepted and may have been exported. Deleting removes the records and the '
          + 'drivers’ photos permanently. This cannot be undone.'
        : 'The tickets and the drivers’ photos will be deleted permanently. This cannot be undone.',
    });
    if (!yes) return;

    setDeleting(true);
    const ids = [...selected];
    let ok = 0;
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i += 5) {
        const batch = ids.slice(i, i + 5);
        const results = await Promise.allSettled(
          batch.map((id) => axios.delete(`${apiUrl}/api/uploads/${id}`)));
        results.forEach((r) => (r.status === 'fulfilled' ? ok++ : failed++));
      }
      if (failed === 0) {
        notify.success(`Deleted ${ok} ticket${ok === 1 ? '' : 's'}`);
      } else {
        notify.warning(`Deleted ${ok}; ${failed} could not be deleted.`);
      }
    } finally {
      setDeleting(false);
      setSelected([]);
      await load();
    }
  };

  /**
   * The deliverable. Five columns, exactly as the office asked — nothing about status, review
   * reasons or upload times, which are our workflow and not theirs.
   *
   * Exports ACCEPTED rows only: an unreviewed row is a machine guess, and shipping it in the same
   * sheet as confirmed ones would make the whole file untrustworthy.
   */
  const exportExcel = () => {
    const accepted = rows.filter((r) => r.status === 'ACCEPTED');
    if (!accepted.length) {
      notify.warning('No accepted tickets in this date range to export.');
      return;
    }
    const aoa = [
      ['Ticket #', 'Truck #', 'Quantity', 'Date', 'Job'],
      ...accepted.map((r) => [
        r.ticketNo || '',
        r.truckNoRaw || '',
        r.quantity ?? '',
        excelDate(r.ticketDate),
        r.job || '',
      ]),
    ];
    // cellDates keeps the Date objects as real date cells, so the office can sort/filter the column.
    // As text it would sort lexicographically and break across a year boundary.
    const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true, dateNF: 'mm/dd/yyyy' });
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
    XLSX.writeFile(wb, `lsj-tickets-${startDate}_${endDate}.xlsx`);
    notify.success(`Exported ${accepted.length} ticket(s)`);
  };

  const acceptedCount = useMemo(() => rows.filter((r) => r.status === 'ACCEPTED').length, [rows]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>
          Ticket Intake {rows.length ? `(${rows.length})` : ''}
        </Typography>
        <Stack direction="row" spacing={1}>
          {selected.length > 0 && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={bulkDelete}
              disabled={deleting}
            >
              Delete selected ({selected.length})
            </Button>
          )}
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportExcel}
            disabled={!acceptedCount}
          >
            Export {acceptedCount ? `(${acceptedCount})` : ''}
          </Button>
        </Stack>
      </Stack>

      {counts && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`Today: ${counts.today}`} />
          <Chip color="warning" label={`Needs review: ${counts.review}`} />
          <Chip color="success" label={`Accepted: ${counts.accepted}`} />
        </Stack>
      )}

      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="date" label="Received from" InputLabelProps={{ shrink: true }}
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              disabled={!dateFiltered}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="date" label="Received to" InputLabelProps={{ shrink: true }}
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
              disabled={!dateFiltered}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ToggleButtonGroup
              size="small" exclusive value={status}
              onChange={(e, v) => v !== null && setStatus(v)}
            >
              {STATUSES.map((s) => (
                <ToggleButton key={s.key || 'all'} value={s.key}>{s.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {!dateFiltered && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Showing every ticket awaiting review, whenever it arrived. A ticket can be in review
                precisely because its date couldn&apos;t be read.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : rows.length === 0 && searched ? (
        <NoMatchingRecords />
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'Select all tickets' }}
                  />
                </TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Ticket date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Ticket #</TableCell>
                <TableCell>Truck</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Job</TableCell>
                <TableCell>Pit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Accepted by</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover selected={selected.includes(r.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.includes(r.id)}
                      onChange={() => toggleOne(r.id)}
                      inputProps={{ 'aria-label': `Select ticket ${r.ticketNo || r.id}` }}
                    />
                  </TableCell>
                  <TableCell>{usDate(r.uploadedAt)}</TableCell>
                  <TableCell>{usDate(r.ticketDate)}</TableCell>
                  <TableCell>{usTime(r.ticketTime)}</TableCell>
                  <TableCell>{r.ticketNo || '—'}</TableCell>
                  {/* Just what was printed. These are subhauler trucks owned by other companies —
                      there is no master to check them against, so annotating every row with a
                      mismatch said nothing and crowded out the real review reasons. */}
                  <TableCell>{r.truckNoRaw || '—'}</TableCell>
                  <TableCell align="right">{r.quantity ?? '—'}</TableCell>
                  <TableCell>{r.job || '—'}</TableCell>
                  <TableCell>{r.pit || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(r.status)} label={r.status} />
                    {r.reviewReason && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.reviewReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.status !== 'ACCEPTED' ? '—'
                      : r.reviewedBy || <Typography variant="caption" color="text.secondary">Automatic</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open ticket photo and edit">
                      <span>
                        <IconButton size="small" onClick={() => openRow(r)} disabled={!r.hasImage && !r.id}>
                          <ImageIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {r.status === 'ACCEPTED' && (
                      <Tooltip title="Send back to review">
                        <IconButton size="small" onClick={() => reopen(r)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete ticket and photo">
                      <IconButton size="small" color="error" onClick={() => remove(r)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={closeRow} maxWidth="lg" fullWidth>
        <DialogTitle>
          Ticket {editing?.ticketNo || editing?.id}
          {editing?.reviewReason && (
            <Typography variant="body2" color="warning.main">{editing.reviewReason}</Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Photo beside the fields: every correction here is someone reading the paper ticket. */}
            <Grid item xs={12} md={7}>
              {imageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : imageUrl ? (
                <Box
                  component="img" src={imageUrl} alt="Ticket scan"
                  sx={{ width: '100%', maxHeight: '65vh', objectFit: 'contain',
                        border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                />
              ) : (
                <Alert severity="info">No photo on file for this ticket.</Alert>
              )}
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <TextField
                  label="Ticket #" size="small" fullWidth value={draft.ticketNo || ''}
                  onChange={(e) => setDraft({ ...draft, ticketNo: e.target.value })}
                />
                <TextField
                  label="Truck #" size="small" fullWidth value={draft.truckNoRaw || ''}
                  onChange={(e) => setDraft({ ...draft, truckNoRaw: e.target.value })}
                  helperText="As printed on the ticket."
                />
                <TextField
                  label="Quantity" size="small" fullWidth type="number" value={draft.quantity ?? ''}
                  onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                />
                <TextField
                  label="Date" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
                  value={draft.ticketDate || ''}
                  onChange={(e) => setDraft({ ...draft, ticketDate: e.target.value })}
                />
                <TextField
                  label="Time" size="small" fullWidth type="time" InputLabelProps={{ shrink: true }}
                  value={draft.ticketTime || ''}
                  onChange={(e) => setDraft({ ...draft, ticketTime: e.target.value })}
                />
                <TextField
                  label="Job" size="small" fullWidth value={draft.job || ''}
                  onChange={(e) => setDraft({ ...draft, job: e.target.value })}
                />
                <TextField
                  label="Pit" size="small" fullWidth value={draft.pit || ''}
                  onChange={(e) => setDraft({ ...draft, pit: e.target.value })}
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRow} disabled={saving}>Cancel</Button>
          <Button onClick={saveDraft} disabled={saving}>Save</Button>
          <Button
            variant="contained" startIcon={<CheckCircleIcon />} onClick={saveAndAccept} disabled={saving}
          >
            Save &amp; Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
